import Big from 'big.js';
import type { MarketDataSnapshot } from '../../../types/MarketDataSnapshot';
import { toBigWithDefault } from '../../../utils/number';
import type { PriceInputMode } from '../PriceQuickOptions';
import { isMarketDataValid } from './isMarketDataValid';
import { isQuickOptionDisabled } from './isQuickOptionDisabled';

export interface CalculatePriceParams {
  option: string;
  marketData?: MarketDataSnapshot;
  priceInputMode?: PriceInputMode;
  priceValue?: string;
  symbol: string;
}

export interface CalculatePriceResult {
  price: Big;
  eventType: 'bid' | 'offer' | 'bps';
  bpsValue?: string;
}

/**
 * Calculates the new price based on the option clicked and current state.
 *
 * This function validates market data validity and option availability before performing the calculation.
 * If validation fails, it returns undefined to indicate the calculation should not proceed.
 *
 * @param {CalculatePriceParams} params - Parameters for price calculation.
 * @returns {CalculatePriceResult | undefined} - The calculated price and event metadata, or undefined if validation fails.
 */
export function calculatePrice({
  option,
  marketData,
  priceInputMode,
  priceValue,
  symbol,
}: CalculatePriceParams): CalculatePriceResult | undefined {
  if (!isMarketDataValid(symbol, marketData)) {
    return undefined;
  }

  if (isQuickOptionDisabled({ optionValue: option, marketData, priceInputMode, priceValue })) {
    return undefined;
  }

  const hasPriceValue = Boolean(priceValue);

  // Handle bid button - use best bid price
  if (option === 'bid') {
    return {
      price: Big(marketData.Bids[0].Price),
      eventType: 'bid',
    };
  }

  // Handle offer button - use best offer price
  if (option === 'offer') {
    return {
      price: Big(marketData.Offers[0].Price),
      eventType: 'offer',
    };
  }

  const priceIncrement = parseFloat(option);
  const isNegativeIncrement = priceIncrement < 0;

  let basePrice: Big;

  // In Increment mode with an existing price, modify the user's current input
  if (priceInputMode === 'Increment' && hasPriceValue) {
    basePrice = toBigWithDefault(priceValue, 0);
  } else {
    // In Reset mode (or Increment mode without a price), calculate from market data
    const relevantSide = isNegativeIncrement ? marketData.Bids : marketData.Offers;
    basePrice = toBigWithDefault(relevantSide?.[0]?.Price, 0);
  }

  // Apply price increment: newPrice = basePrice * (1 + priceIncrement)
  const price = basePrice.times(Big(1).plus(priceIncrement));

  return {
    price,
    eventType: 'bps',
    bpsValue: option,
  };
}
